import { LOCK_MODEL_IDS, MODEL_FEATURES, MODEL_LABEL as UI_MODEL_LABEL, modelLabel, parseModel } from '@antreja/ui/models';
import type { LockModel } from './types';

const TZ = 'Europe/Amsterdam';

const dateFmt = new Intl.DateTimeFormat('nl-NL', { day: 'numeric', month: 'short', year: 'numeric', timeZone: TZ });
const dateTimeFmt = new Intl.DateTimeFormat('nl-NL', {
  day: 'numeric',
  month: 'short',
  hour: '2-digit',
  minute: '2-digit',
  timeZone: TZ,
});
const weekdayDateTimeFmt = new Intl.DateTimeFormat('nl-NL', {
  weekday: 'short',
  day: 'numeric',
  month: 'short',
  hour: '2-digit',
  minute: '2-digit',
  timeZone: TZ,
});

export const fmtDate = (iso: string | null | undefined) => (iso ? dateFmt.format(new Date(iso)) : '—');
export const fmtDateTime = (iso: string | null | undefined) => (iso ? dateTimeFmt.format(new Date(iso)) : '—');
export const fmtWeekdayDateTime = (iso: string) => weekdayDateTimeFmt.format(new Date(iso));

/** "3 uur", "2 dagen", "12 min" — used for "wacht op sync" age. */
export function fmtAge(iso: string | null | undefined, now = new Date()): string {
  if (!iso) return '—';
  const ms = Math.max(0, now.getTime() - new Date(iso).getTime());
  const min = Math.floor(ms / 60000);
  if (min < 1) return 'net';
  if (min < 60) return `${min} min`;
  const h = Math.floor(min / 60);
  if (h < 48) return `${h} uur`;
  return `${Math.floor(h / 24)} dagen`;
}

export function fmtAgo(iso: string | null | undefined, now = new Date()): string {
  if (!iso) return 'nooit';
  const age = fmtAge(iso, now);
  return age === 'net' ? 'zojuist' : `${age} geleden`;
}

/** Zichtbare modelnamen (A1V/A1T/A1K) komen uit één centrale plek; intern blijven D1/D1_R/D1_K. */
export const MODEL_LABEL: Readonly<Record<LockModel, string>> = UI_MODEL_LABEL;
export const MODEL_HINT: Readonly<Record<LockModel, string>> = MODEL_FEATURES;
export const MODELS: readonly LockModel[] = LOCK_MODEL_IDS;
export { modelLabel, parseModel };

export function toDateInput(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export function toDateTimeInput(d: Date): string {
  return `${toDateInput(d)}T${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

/**
 * Eén CSV-cel. Tekst die met = + - @ (of tab/CR) begint krijgt een ' ervoor, zodat Excel er geen formule van maakt
 * (CSV-injectie, security review L4). Getallen blijven getallen.
 */
export function csvCell(v: string | number | null): string {
  let s = v === null ? '' : String(v);
  if (typeof v === 'string' && /^[=+\-@\t\r]/.test(s)) s = `'${s}`;
  return /[;"\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

export function downloadCsv(filename: string, header: string[], rows: (string | number | null)[][]) {
  const body = [header, ...rows].map((r) => r.map(csvCell).join(';')).join('\r\n');
  const blob = new Blob(['﻿' + body], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}
